import React, { useEffect, useLayoutEffect, useState } from "react";

import * as Sentry from "@sentry/react";
import { useLocation } from "react-router-dom";
import { useNavigate, useSearchParams } from "react-router-dom";

import Stack from "@mui/material/Stack";

import AuthCardBanner from "@app/analytics/auth/AuthCardBanner";
import BackButton from "@app/analytics/auth/BackButton";
import AuthEmail from "@app/analytics/auth/components/AuthEmail";
import OAuthDetectedContent from "@app/analytics/auth/SignupCard/OAuthDetectedContent";
import {
  DOMAIN_ERROR_TOAST,
  DUPLICATE_ACCOUNT_TOAST,
  isDomainError,
} from "@app/analytics/auth/utils";
import useAuth from "@app/common/auth/useAuth";
import { SSO_PROVIDERS_BY_ID } from "@app/common/constants/providers";
import { showToast, hideToast } from "@app/common/hooks/useToasts";
import ROUTES from "@app/common/routes";
import type {
  LocationWithState,
  EmailPassSignUpData,
  SSOProviderId,
} from "@app/common/types";
import { withQueryParams } from "@app/common/utils";
import { usePageTitle } from "@app/utils/hooks";

import EmailContent from "./EmailContent";
import PasswordContent from "./PasswordContent";
import { getProviderFromEmail } from "./signUpUtils";

// This method is begging us to have separate routes
// and components for the sign-up flow!
const getPageTitle = ({
  sessionId,
  inviteId,
  showPasswordContent,
  showProviderContent,
}: {
  sessionId: string | null;
  inviteId: string | null;
  showEmailContent: boolean;
  showPasswordContent: boolean;
  showProviderContent: boolean;
}): string => {
  if (showProviderContent) return "SSO sign up option";
  if (showPasswordContent) return "Create a password";
  if (inviteId) return "Join a Workspace";
  if (sessionId) return "Access this Meeting Report";
  return "";
};

const getHeaderText = ({
  sessionId,
  inviteId,
}: {
  sessionId?: string | null;
  inviteId?: string | null;
}) => {
  if (inviteId) {
    return "Create an account to join this Workspace";
  }
  if (sessionId) {
    return "Create an account to access this Meeting Report";
  }
  return null;
};

const Signup = ({ withNav }: { withNav?: boolean }) => {
  const navigate = useNavigate();
  const location = useLocation() as LocationWithState;
  const auth = useAuth();

  const [queryParams] = useSearchParams();
  const sessionId = queryParams.get("sessionId");
  const inviteId = queryParams.get("inviteId");
  const email = queryParams.get("email");
  const zoomRedirect = queryParams.get("zoomRedirect");
  const redirectTo = queryParams.get("redirectTo");
  const errorReason = queryParams.get("reason");
  const accountProviderId = queryParams.get("p") as
    | SSOProviderId
    | "email"
    | null;

  const accountProvider =
    accountProviderId && accountProviderId !== "email"
      ? SSO_PROVIDERS_BY_ID[accountProviderId]
      : null;

  const [loading, setLoading] = useState(false);

  useEffect(() => {
    // If user already exists for this email,
    // direct to the sign-in page
    if (auth.errors.signUp?.email) {
      // clear state to avoid redirect loops
      auth.setErrors({ signUp: {} });
      navigate(withQueryParams(ROUTES.SIGN_IN, { error: "account_exists" }));
    }
  }, [auth.errors.signUp?.email]);

  // If we already have the email then
  // forward to the appropriate provider
  useLayoutEffect(() => {
    if (!accountProviderId && email) {
      handleContinueWithEmail(email).catch((error) => {
        Sentry.captureException(error);
        setLoading(false);
      });
    }
  }, [accountProviderId, email]);

  useEffect(() => {
    if (errorReason === "domain_capture") {
      showToast(DOMAIN_ERROR_TOAST);
    } else if (errorReason === "duplicate") {
      showToast(DUPLICATE_ACCOUNT_TOAST);
    }
  }, [errorReason]);

  useEffect(() => {
    if (!inviteId) return;
    showToast({
      message:
        "To accept your Workspace invitation, please create a Read account.",
      sticky: true,
      dismissible: false,
    });
    return () => hideToast();
  }, [inviteId]);

  const handlePasswordSubmit = (data: EmailPassSignUpData) =>
    auth.signUp(data, {
      onError: async (error) => {
        const isError = await isDomainError(error);
        if (isError) {
          showToast(DOMAIN_ERROR_TOAST);
        }
      },
    });

  const handleOauthClick = (provider: SSOProviderId) =>
    auth.signUp({ action: provider });

  const handleContinueWithEmail = async (email: string) => {
    setLoading(true);
    const provider = await getProviderFromEmail(email);
    navigate(
      withQueryParams(ROUTES.SIGN_UP, { p: provider, email }, location.search),
      {
        replace: true,
        state: location.state,
      }
    );
    setLoading(false);
  };

  const handleReset = () => {
    navigate(
      withQueryParams(ROUTES.SIGN_UP, undefined, location.search, [
        "p",
        "email",
      ]),
      {
        replace: true,
        state: location.state,
      }
    );
  };

  const handleUsePassword = () =>
    navigate(withQueryParams(ROUTES.SIGN_UP, { p: "email" }, location.search), {
      replace: true,
      state: {
        ...location.state,
        ...(zoomRedirect || redirectTo // override redirectTo if we have a zoomRedirect or redirectTo param
          ? { redirectTo: { pathname: zoomRedirect || redirectTo } }
          : {}),
      },
    });

  let showEmailContent =
    accountProviderId === null || (accountProviderId !== "email" && !email);
  const showPasswordContent = accountProviderId === "email" && email;
  const showProviderContent =
    !showEmailContent &&
    !showPasswordContent &&
    email &&
    accountProviderId &&
    accountProviderId !== "email";

  // fallback to show email content if nothing else would be shown
  showEmailContent = !showPasswordContent && !showProviderContent;

  const pageTitle = getPageTitle({
    sessionId,
    inviteId,
    showEmailContent,
    showPasswordContent: Boolean(showPasswordContent),
    showProviderContent: Boolean(showProviderContent),
  });

  usePageTitle(`Sign up${pageTitle ? ` - ${pageTitle}` : ""}`);
  const bannerText = getHeaderText({ sessionId, inviteId });

  return (
    <>
      {!email && bannerText && <AuthCardBanner>{bannerText}</AuthCardBanner>}
      <Stack spacing={3} sx={{ width: "100%" }}>
        {email && <AuthEmail provider={accountProvider} email={email} />}
        {showEmailContent && (
          <EmailContent
            loading={loading}
            onEmailContinue={handleContinueWithEmail}
            onOAuthContinue={handleOauthClick}
          />
        )}
        {showPasswordContent && (
          <PasswordContent
            email={email}
            onBack={handleReset}
            onSubmit={handlePasswordSubmit}
          />
        )}
        {showProviderContent && (
          <OAuthDetectedContent
            onBack={handleReset}
            onClickContinue={handleOauthClick}
            onPreferPassword={handleUsePassword}
            providerId={accountProviderId}
          />
        )}
      </Stack>
      {withNav && <BackButton />}
    </>
  );
};

export default Signup;
